import React, { useContext, useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import { CreditCard as CreditCardIcon, Lock as LockIcon } from '@mui/icons-material';
import { AuthContext } from '../App';

const Login: React.FC = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  // Redirect after login based on user type
  useEffect(() => {
    if (auth?.user && !loading) {
      if (auth.user.is_staff) {
        navigate('/admin', { replace: true });
      } else {
        navigate('/cards', { replace: true });
      }
    }
  }, [auth?.user, loading, navigate]);

  if (auth?.user) {
    // Redirect admin to /admin, normal users to /cards
    if (auth.user.is_staff) {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/cards" replace />;
  }

  // Real-time validation helper
  const validateField = (fieldName: string, value: string): string | null => {
    switch (fieldName) {
      case 'username':
        if (!value.trim()) return 'Username is required.';
        return null;
      case 'password':
        if (!value) return 'Password is required.';
        return null;
      default:
        return null;
    }
  };

  const handleFieldBlur = (fieldName: string, value: string) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    const error = validateField(fieldName, value);
    if (error) {
      setFieldErrors(prev => ({ ...prev, [fieldName]: error }));
    } else {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleFieldChange = (fieldName: string, value: string, setter: (value: string) => void) => {
    setter(value);
    // Clear error when user starts typing
    if (fieldErrors[fieldName] && touchedFields[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
    // Clear general error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    
    // Mark all fields as touched
    setTouchedFields({
      username: true,
      password: true,
    });

    // Client-side validation
    const newFieldErrors: Record<string, string> = {};
    const usernameError = validateField('username', username);
    const passwordError = validateField('password', password);
    
    if (usernameError) newFieldErrors.username = usernameError;
    if (passwordError) newFieldErrors.password = passwordError;

    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    try {
      // Login through auth context (this will update user state)
      await auth?.login(username.trim(), password);
      // The redirect will happen automatically via useEffect when user state updates
      // No need to manually navigate here
    } catch (err: any) {
      setLoading(false);
      
      // Handle field-specific errors from backend
      const backendErrors = err?.response?.data || {};
      const backendFieldErrors: Record<string, string> = {};
      
      if (backendErrors.username) {
        backendFieldErrors.username = Array.isArray(backendErrors.username) 
          ? backendErrors.username[0] 
          : backendErrors.username;
      }
      if (backendErrors.password) {
        backendFieldErrors.password = Array.isArray(backendErrors.password) 
          ? backendErrors.password[0] 
          : backendErrors.password;
      }
      
      // Set field errors if any
      if (Object.keys(backendFieldErrors).length > 0) {
        setFieldErrors(backendFieldErrors);
      }
      
      // Set general error message
      const generalError = 
        backendErrors.detail ||
        backendErrors.non_field_errors?.[0] ||
        (Object.keys(backendFieldErrors).length === 0 
          ? 'Invalid username or password. Please check your credentials and try again.'
          : 'Please fix the errors below and try again.');
      setError(generalError);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
        display: 'flex',
        alignItems: 'center',
        py: 6,
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CreditCardIcon sx={{ color: 'white' }} />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={800}>
                    Credit Card Optimizer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Trinidad & Tobago
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Sign in
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use your account to access smart recommendations and savings insights.
                </Typography>
              </Box>

              {error && <Alert severity="error">{error}</Alert>}

              <Box component="form" onSubmit={onSubmit}>
                <Stack spacing={2}>
                  <TextField
                    label="Username"
                    value={username}
                    onChange={(e) => handleFieldChange('username', e.target.value, setUsername)}
                    onBlur={(e) => handleFieldBlur('username', e.target.value)}
                    autoComplete="username"
                    fullWidth
                    required
                    error={Boolean(fieldErrors.username && touchedFields.username)}
                    helperText={touchedFields.username ? fieldErrors.username : ''}
                  />
                  <TextField
                    label="Password"
                    value={password}
                    onChange={(e) => handleFieldChange('password', e.target.value, setPassword)}
                    onBlur={(e) => handleFieldBlur('password', e.target.value)}
                    type="password"
                    autoComplete="current-password"
                    fullWidth
                    required
                    error={Boolean(fieldErrors.password && touchedFields.password)}
                    helperText={touchedFields.password ? fieldErrors.password : ''}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !username || !password}
                    startIcon={<LockIcon />}
                    sx={{
                      background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                      py: 1.3,
                    }}
                  >
                    {loading ? 'Signing in…' : 'Sign in'}
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => navigate('/signup')}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    Don&apos;t have an account? Sign up
                  </Button>
                </Stack>
              </Box>

              
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;

